import React from 'react';
import { MessageSquare, Send, X } from 'lucide-react';
import { Loader } from '../../atoms/Loader';

type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'white'
  | 'dark'
  | 'discord'
  | 'background';

type ButtonSize = 'sm' | 'md' | 'lg';
type ButtonRounded = 'md' | 'full';
type ButtonShadow = 'none' | 'md' | 'xl' | '2xl';
type ButtonVariant = 'solid' | 'ghost';

const iconMap = {
  'message-square': MessageSquare,
  send: Send,
  x: X,
} as const;

const isMappedIcon = (value: unknown): value is keyof typeof iconMap =>
  typeof value === 'string' && value in iconMap;

interface CustomButtonProps {
  onClick?: () => void;
  title?: string;
  disabled?: boolean;
  icon?: React.ReactNode | keyof typeof iconMap;
  color?: ButtonColor;
  variant?: ButtonVariant;
  customColor?: string;
  loading?: boolean;
  size?: ButtonSize;
  iconSize?: number;
  rounded?: ButtonRounded;
  shadow?: ButtonShadow;
  iconOnly?: boolean;
  fullWidth?: boolean;
  floating?: boolean;
  type?: 'button' | 'submit' | 'reset';
  ariaLabel?: string;
  className?: string;
}

export const CustomButton: React.FC<CustomButtonProps> = ({
  onClick,
  title = '',
  disabled = false,
  icon,
  color = 'primary',
  variant = 'solid',
  customColor,
  loading = false,
  size = 'md',
  iconSize,
  rounded = 'md',
  shadow = 'none',
  iconOnly = false,
  fullWidth = false,
  floating = false,
  type = 'button',
  ariaLabel,
  className = '',
}) => {
  const colorStyles = {
    primary: 'bg-accent hover:bg-accent-hover text-white',
    secondary: 'bg-secondary-500 hover:bg-secondary-600 text-white',
    white: 'bg-raised hover:bg-line-soft text-content',
    dark: 'bg-soft hover:bg-content/80 text-surface',
    discord: 'bg-[#7289da] hover:bg-[#5b6eae] text-white',
    background: 'bg-warm hover:bg-raised text-soft',
  };

  const sizeStyles = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
  };

  const paddingStyles = {
    sm: iconOnly ? 'p-2' : 'px-3 py-1.5',
    md: iconOnly ? 'p-3' : 'px-4 py-2',
    lg: iconOnly ? 'p-4' : 'px-5 py-3',
  };

  const roundedStyles = {
    md: 'rounded-lg',
    full: 'rounded-full',
  };

  const shadowStyles = {
    none: '',
    md: 'shadow-md',
    xl: 'shadow-xl',
    '2xl': 'shadow-2xl',
  };

  const iconSizes = {
    sm: 16,
    md: 18,
    lg: 22,
  };

  const ghostColorStyles = {
    primary: 'text-primary-500 hover:bg-primary-500/10',
    secondary: 'text-secondary-500 hover:bg-secondary-500/10',
    white: 'text-content hover:bg-raised',
    dark: 'text-soft hover:bg-raised',
    discord: 'text-[#7289da] hover:bg-[#7289da]/10',
    background: 'text-soft hover:bg-raised',
  };

  const resolvedIcon = isMappedIcon(icon)
    ? (() => {
        const IconComponent = iconMap[icon];
        return <IconComponent size={iconSize ?? iconSizes[size]} />;
      })()
    : icon;

  const customStyle = customColor
    ? {
        backgroundColor: disabled || loading ? '#ccc' : customColor,
      }
    : undefined;

  const resolvedAriaLabel =
    ariaLabel || title || (typeof icon === 'string' ? icon : undefined);

  return (
    <button
      onClick={onClick}
      type={type}
      disabled={disabled || loading}
      aria-label={resolvedAriaLabel}
      style={customStyle}
      className={`inline-flex min-w-fit items-center justify-center gap-2 font-medium transition-all duration-200 hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${
        customColor
          ? 'text-white hover:opacity-80'
          : variant === 'ghost'
            ? ghostColorStyles[color]
            : colorStyles[color]
      } ${sizeStyles[size]} ${paddingStyles[size]} ${roundedStyles[rounded]} ${shadowStyles[shadow]} ${
        fullWidth ? 'w-full' : ''
      } ${floating ? 'fixed right-6 bottom-6 z-50 hover:scale-110' : ''} ${className}`}
    >
      {loading ? <Loader /> : resolvedIcon}
      {!loading && !iconOnly && title && (
        <span className="text-center">{title}</span>
      )}
    </button>
  );
};
